import { saveAs } from 'file-saver';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { centsToXlsxNumber, applyCurrencyFormat } from '../../../helpers/excelExportHelpers';
import { getTransactionSourceLabel } from './getTransactionSourceLabel';

/**
 * Export transactions report data to XLSX file
 *
 * COLUMN ORDER IS FROZEN. This sheet mirrors the FareHarbor transactions export
 * column-for-column so the client can reconcile bookings against it. Do not
 * reorder these columns to match the on-screen table or the other reports - the
 * screen may be standardized, this export may not. Any change here needs client
 * sign-off first.
 *
 * @param {object} reportData - The report data from the API
 * @param {string} dateFrom - Start date string
 * @param {string} dateTo - End date string
 * @param {boolean} taxBreakout - Whether to include tax breakout columns
 * @param {Function} t - i18next translation function (for the Source column)
 */
export const exportTransactionsReportToXlsx = async (
    reportData,
    dateFrom,
    dateTo,
    taxBreakout = false,
    t = (key) => key,
) => {
    const XLSX = await import('xlsx');
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    // Build the data array for the sheet
    const sheetData = [];

    // Add header with date range and total
    sheetData.push(['Transactions Report']);
    sheetData.push([
        `Date Range: ${new Date(dateFrom + 'T12:00:00').toLocaleDateString()} - ${new Date(dateTo + 'T12:00:00').toLocaleDateString()}`,
    ]);
    const summaryRow = [`Total Records: ${reportData.total || 0}`];
    if (taxBreakout && reportData.totalNetAmount !== undefined) {
        summaryRow.push(
            `Net Amount: ${formatCentsToUsd(reportData.totalNetAmount || 0)}`,
            `Tax Amount: ${formatCentsToUsd(reportData.totalTaxAmount || 0)}`,
            `Total Amount: ${formatCentsToUsd(reportData.totalTransactionAmount || 0)}`,
            `Processing Fees: ${formatCentsToUsd(reportData.totalProcessingFees || 0)}`,
            `Absorbed Processing Fees: ${formatCentsToUsd(reportData.totalAbsorbedProcessingFees || 0)}`,
        );
    } else {
        summaryRow.push(
            `Total Amount: ${formatCentsToUsd(reportData.totalTransactionAmount || 0)}`,
            `Processing Fees: ${formatCentsToUsd(reportData.totalProcessingFees || 0)}`,
            `Absorbed Processing Fees: ${formatCentsToUsd(reportData.totalAbsorbedProcessingFees || 0)}`,
        );
    }
    sheetData.push(summaryRow);
    sheetData.push([]); // Empty row

    // Add column headers
    const headers = [
        'Booking ID',
        'Booking Status',
        'Show',
        'Contact',
        'Email',
        'Voucher',
        'Customers',
        'Customer Count',
        'Show Date',
        'Order ID',
        'Order Amount',
        'Order Status',
        'Transaction ID',
        'Transaction Amount',
        'Transaction Status',
        'Payment Type',
        'Source',
        'Transaction Date',
        'Processing Fee',
        'Absorbed Processing Fee',
    ];

    if (taxBreakout) {
        headers.push('Net Amount', 'Tax Amount');
    }

    sheetData.push(headers);

    // Add data rows
    reportData.items.forEach((item) => {
        const row = [
            item.bookingId,
            item.bookingStatus || '',
            item.showTitle,
            item.contact,
            item.email,
            item.voucher,
            item.customers,
            item.customerCount,
            item.availability,
            item.orderId,
            centsToXlsxNumber(item.orderAmount),
            item.orderStatus,
            item.transactionId,
            centsToXlsxNumber(item.transactionAmount),
            item.transactionFinished ? 'Finished' : 'Pending',
            item.transactionPaymentMethodName || item.transactionPaymentType,
            getTransactionSourceLabel(item, t),
            item.transactionDate,
            centsToXlsxNumber(item.processingFee),
            centsToXlsxNumber(item.absorbedProcessingFee),
        ];

        if (taxBreakout) {
            row.push(centsToXlsxNumber(item.netAmount), centsToXlsxNumber(item.taxAmount));
        }

        sheetData.push(row);
    });

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Set column widths
    const cols = [
        { wch: 12 }, // Booking ID
        { wch: 15 }, // Booking Status
        { wch: 25 }, // Show
        { wch: 25 }, // Contact
        { wch: 30 }, // Email
        { wch: 12 }, // Voucher
        { wch: 30 }, // Customers
        { wch: 15 }, // Customer Count
        { wch: 20 }, // Show Date
        { wch: 10 }, // Order ID
        { wch: 15 }, // Order Amount
        { wch: 12 }, // Order Status
        { wch: 15 }, // Transaction ID
        { wch: 18 }, // Transaction Amount
        { wch: 18 }, // Transaction Status
        { wch: 15 }, // Payment Type
        { wch: 16 }, // Source
        { wch: 20 }, // Transaction Date
        { wch: 15 }, // Processing Fee
        { wch: 22 }, // Absorbed Processing Fee
    ];

    if (taxBreakout) {
        cols.push({ wch: 12 }, { wch: 12 }); // Net Amount, Tax Amount
    }

    ws['!cols'] = cols;

    // Set currency format for Order Amount, Transaction Amount, Processing Fee,
    // Absorbed Processing Fee, and tax-breakout columns
    const currencyCols = [10, 13, 18, 19];
    if (taxBreakout) {
        currencyCols.push(20, 21);
    }
    applyCurrencyFormat(XLSX, ws, sheetData, currencyCols);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions Report');

    // Generate file
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: fileType });

    // Generate filename with date range
    const filename = `transactions-report-${dateFrom}-to-${dateTo}${fileExtension}`;
    saveAs(file, filename);
};

export default exportTransactionsReportToXlsx;
